import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, UserCheck, Building, Mail, Phone, Calendar, LogOut, Clock } from 'lucide-react';
import { adminAuth } from '../services/adminAuth';

const AdminAgentOutreach: React.FC = () => {
  const navigate = useNavigate();

  // Check authentication on component mount
  useEffect(() => {
    if (!adminAuth.isAuthenticated()) {
      navigate('/admin-login');
      return;
    }
  }, [navigate]);

  const handleSignOut = () => {
    adminAuth.logout();
    navigate('/admin-login');
  };

  if (!adminAuth.isAuthenticated()) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link
              to="/admin"
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
              <UserCheck className="w-6 h-6" />
              Agent Outreach
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={handleSignOut}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <LogOut className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Coming Soon Hero */}
        <div className="bg-white rounded-lg shadow-sm p-12 text-center mb-8">
          <div className="max-w-2xl mx-auto">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-indigo-100 rounded-full mb-6">
              <Clock className="w-10 h-10 text-indigo-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Agent Outreach Coming Soon
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              We're building powerful tools to help you connect with real estate agents and expand your business network. 
              Stay tuned for advanced agent management features.
            </p>
            <div className="inline-flex items-center gap-2 bg-indigo-50 text-indigo-700 px-4 py-2 rounded-lg">
              <Calendar className="w-4 h-4" />
              Expected Launch: Q2 2024
            </div>
          </div>
        </div>

        {/* Planned Features */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Building className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Agent Directory</h3>
            </div>
            <p className="text-gray-600">
              Comprehensive database of real estate agents with contact information, specialties, and performance metrics.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Mail className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Email Campaigns</h3>
            </div>
            <p className="text-gray-600">
              Create and send targeted email campaigns to agents with personalized property listings and service offerings.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Phone className="w-5 h-5 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Call Management</h3>
            </div>
            <p className="text-gray-600">
              Track outreach calls, schedule follow-ups, and manage agent relationships with integrated CRM features.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-orange-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Agent Profiles</h3>
            </div>
            <p className="text-gray-600">
              Detailed agent profiles with transaction history, client reviews, and collaboration opportunities.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Meeting Scheduler</h3>
            </div>
            <p className="text-gray-600">
              Schedule and manage meetings with agents, track appointments, and set automated reminders.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                <Building className="w-5 h-5 text-teal-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Partnership Tracking</h3>
            </div>
            <p className="text-gray-600">
              Monitor partnership performance, track referrals, and manage commission structures with agents.
            </p>
          </div>
        </div>

        {/* Notification Signup */}
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg shadow-sm p-8 text-white">
          <div className="max-w-2xl mx-auto text-center">
            <h3 className="text-2xl font-bold mb-4">
              Get Notified When Agent Outreach Launches
            </h3>
            <p className="text-indigo-100 mb-6">
              Be the first to know when our powerful agent outreach tools are ready. 
              We'll send you an update as soon as the feature is available.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-2 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white"
              />
              <button className="bg-white text-indigo-600 px-6 py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors">
                Notify Me
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAgentOutreach; 